import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { ChannelsService } from '../../../services/channels.service';
import { ProvidersService } from '../../../services/providers.service';
import { AlertService } from '../../../services/alert.service';
import { useLocation, useNavigate } from 'react-router-dom';
import ActionsDropdown from '../../../components/datatable/actions-dropdown.component';
import Header from '../../../components/layout/header.component';
import Asaid from '../../../components/layout/asaid.component';
import Footer from '../../../components/layout/footer.component';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import AddAccount from './modal/add-account.modal';
import SendMessage from '../wa/modal/send-message.modal';
import Datatable from '../../../components/datatable/datatable.component';
import useRequest from '../../../hooks/use-request.hook';
import Icon from '../../../components/forms/icon.component';
import PrincipalCard from '../../../components/cards/principal-card.component';
import { t } from 'i18next';
import DateRegister from '../../../components/datatable/register-data.component';
import { useSession } from '../../../hooks/use-session.hook';
import { PROFILES } from '../../../constants';
import LogData from '../../../components/logs/log-data.component';

interface Data {
  _id: string;
  created_at: string;
  status: boolean;
  provider: string;
  name: string;
}

const StatusBadge = ({ status }: { status: true | false }) => {
  switch (status) {
    case true:
      return (
        <span className="badge text-dark fs-6">
          <Icon icon="ki-check-circle" size="fs-1" type="primary" />
          {t('general.active')}
        </span>
      );
    case false:
      return (
        <span className="badge text-danger fs-6">
          <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
          {t('general.inactive')}
        </span>
      );
    default:
      return <span className="badge badge-secondary">Unknown</span>;
  }
};

function ChannelWaPage() {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const { session, selectedAccount } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isFromTemplates = location.pathname.includes('/templates-panel/accounts-wa');
  const [totalAccounts, settotalAccounts] = useState<number>(0);
  const [totalActives, settotalActives] = useState<number>(0);
  const [providers, setProviders] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<Data[]>([]);

  const [showModal, setShowModal] = useState({
    show: false,
    data: null
  });

  const [showModalSendMessage, setShowModalSendMessage] = useState({
    show: false,
    data: null
  });

  const dataStatistics = [
    { label: 'Cuentas', total: totalAccounts, type: '', icon: 'ki-book-open' },
    { label: 'Activas', total: totalActives, type: 'primary', icon: 'ki-check-circle' }
  ];

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowModal({ show: false, data: null });
  };

  const handleShowModalSendMessage = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowModalSendMessage({ show: false, data: null });
  };

  const renderStatusCell = (row: Data) => <StatusBadge status={row.status} />;

  const columns = [
    {
      name: t('channels.wa.data-account'),
      sortable: true,
      selector: (r: any) => `${r.name} meta`,
      width: 20,
      cell: (account: any) => {
        const provider = providers.find((p) => p.value === account.provider);
        return (
          <ul className="list-group-item">
            <li>
              <div className="d-flex flex-column">
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('channels.wa.name')}:</strong>
                  <span className="ms-1">{account.name || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <strong className="fs-6">{t('channels.wa.provider')}:</strong>
                  {provider && provider.label === 'Meta' && (
                    <>
                      <span className="d-none">meta</span>
                      <span className="m-n2 theme-light-show">
                        <img
                          src="/assets/media/logos/meta-large.svg"
                          alt="Proveedor"
                          width={'100px'}
                          height={'40px'}
                        />
                      </span>
                      <span className="m-n2 theme-dark-show">
                        <img
                          src="/assets/media/logos/meta-large-dark.svg"
                          alt="Proveedor"
                          width={'100px'}
                          height={'40px'}
                        />
                      </span>
                    </>
                  )}
                </div>
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: 'Status',
      sortable: true,
      center: true,
      selector: (r: any) => `${r.status} activo inactivo`,
      width: 20,
      cell: renderStatusCell
    },
    {
      name: t('channels.wa.data-config'),
      sortable: true,
      selector: (r: any) =>
        `${r.settings.phone_number} ${r.settings.phone_number_id} ${r.settings.waba_id} ${r.settings.app_id} ${r.settings.token}`,
      width: 30,
      cell: (account: any) => {
        const phone_number = account.settings.phone_number || t('general.no-info');
        const countryCode = phone_number !== t('general.no-info') ? phone_number.slice(0, 2) : '';
        const restOfNumber = phone_number !== t('general.no-info') ? phone_number.slice(2) : '';

        const phone_number_id = account.settings.phone_number_id || t('general.no-info');
        const waba_id = account.settings.waba_id || t('general.no-info');
        const app_id = account.settings.app_id || t('general.no-info');
        const token = account.settings.token || t('general.no-info');

        return (
          <ul className="list-group-item">
            <li className="list-group-item">
              <div className="d-flex flex-column">
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('channels.wa.number')}:</strong>
                  <span className="ms-1 fs-6">
                    {countryCode === '52' && phone_number !== t('general.no-info') && (
                      <img
                        className="rounded-circle me-2"
                        src="/assets/media/flags/mexico.svg"
                        alt="México"
                        width={'14px'}
                        height={'14px'}
                      />
                    )}
                    {phone_number !== t('general.no-info')
                      ? `+${countryCode} ${restOfNumber}`
                      : t('general.no-info')}
                  </span>
                </div>
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('channels.wa.id-number')}:</strong>
                  <span className="ms-1 fs-6">{phone_number_id}</span>
                </div>
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('channels.wa.id-waba')}:</strong>
                  <span className="ms-1 fs-6">{waba_id}</span>
                </div>
                <div className="w-100">
                  <strong className="fs-6">{t('channels.wa.id-app')}:</strong>
                  <span className="ms-1 fs-6">{app_id}</span>
                </div>
                <div className="w-100">
                  <strong className="fs-6">{t('channels.wa.token')}:</strong>
                  <span className="ms-1 fs-6 d-inline-block text-break">{token}</span>
                </div>
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: t('channels.wa.data-date'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      width: 15,
      cell: (account: any) => {
        return <DateRegister created_at={account.created_at} />;
      }
    },
    ...[
      {
        name: t('general.acctions'),
        sortable: true,
        selector: (r: any) => `${r._id}`,
        center: true,
        exportable: false,
        width: 20,
        cell: (account: any) => {
          if (isFromTemplates) {
            return (
              <button
                className="btn btn-light btn-sm hover-scale fw-bolder"
                type="button"
                onClick={() => {
                  navigate('/templates-panel/templates-wa', {
                    state: {
                      wabaId: account._id,
                      accountName: account.name
                    }
                  });
                }}
              >
                <Icon icon="ki-picture" size="fs-2" type="primary" />
                Ver plantillas
              </button>
            );
          }
          if (session.profile !== PROFILES.ADMIN) {
            return (
              <ActionsDropdown
                rowStatus={account.status}
                edit={() => setShowModal({ show: true, data: account })}
                send={() => setShowModalSendMessage({ show: true, data: account })}
                changeStatus={() =>
                  changeOutputStatus(account._id, 'changeStatusWA-9994734567', account.status)
                }
              />
            );
          } else {
            return <div>-</div>;
          }
        }
      }
    ]
  ];

  const changeOutputStatus = async (id: string, buttonId: string, status: boolean) => {
    const messageQuestion = status ? t('channels.wa.q7') : t('channels.wa.q6');

    const isConfirmed = await AlertService.question(
      t('general.atention'),
      messageQuestion + '<br><br>',
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;

    status = !status;
    try {
      await call(() => ChannelsService.changeStatusWA({ _id: id, status }));
      AlertService.toastSuccess(t('channels.wa.success4'));
      await getAccounts();
      LogData({ id: id, status: status ? 'enable' : 'disable' }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getAccounts = async () => {
    try {
      if (session.profile === PROFILES.ROOT) {
        const response = await call(() => ChannelsService.getAccountsWhatsApp());
        const accountsData: Data[] = response;
        settotalActives(accountsData.filter((channel) => channel.status === true).length);
        settotalAccounts(accountsData.length);
        setAccounts(accountsData);
      } else if (session.profile === PROFILES.ADMIN) {
        const response = await call(() =>
          ChannelsService.getAccountsWhatsAppForAccount(selectedAccount.account_id)
        );
        const accountsData: Data[] = response;
        settotalActives(accountsData.filter((channel) => channel.status === true).length);
        settotalAccounts(accountsData.length);
        setAccounts(accountsData);
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getProviders = async () => {
    try {
      const data = await ProvidersService.getProviders();
      setProviders(
        data.map((item: any) => ({
          value: item._id,
          label: item.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getProviders();
        await getAccounts();
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  btnCreate={session.profile !== PROFILES.ADMIN && !isFromTemplates}
                  onClick={() => setShowModal({ show: true, data: null })}
                  btnRefresh
                  onRefresh={async () => {
                    await getAccounts();
                    AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                  }}
                  btnBack
                  title="Cuentas"
                  titleBolder="WhatsApp"
                  subtitle={
                    isFromTemplates
                      ? 'Crea tus plantillas en la cuenta de WhatsApp que necesites.'
                      : 'Configura y gestiona tus cuentas para hacer envíos.'
                  }
                  breadcrumbItems={
                    isFromTemplates ? ['Herramientas', 'Plantillas'] : ['Operaciones', 'Canales']
                  }
                  textButton="Agregar cuenta"
                  iconButton="ki-soft-2"
                />
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header align-items-center border-0 mt-5">
                    <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                      <span className="fw-bolder fw-bold fs-2">Cuentas</span>
                      <span className="text-gray-700 mt-2 fw-semibold fs-6">Registradas</span>
                    </div>
                    <div className="card-toolbar"></div>
                  </div>
                  <div className="card-body pt-1">
                    <Datatable columns={columns} data={accounts} isLoading={isRequesting} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          <SendMessage
            show={showModalSendMessage.show}
            payload={showModalSendMessage.data}
            onRequestClose={(refresh: any) => handleShowModalSendMessage(refresh)}
          />
          <AddAccount
            show={showModal.show}
            onRequestClose={(refresh: any) => handleShowModal(refresh)}
            payload={showModal.data}
          />
        </div>
      </div>
    </>
  );
}

export default ChannelWaPage;
